/**
 * Socket Handler Manager
 *
 * Consolidates all socket.on() listeners from OceanScene.
 * Organizes handlers by feature: ships, NPCs, combat, fishing, whirlpools, etc.
 */

import Phaser from "phaser";
import { Socket } from "socket.io-client";
import { ShipData, NPCData, TradeRouteData } from "../types";
import { ShopData } from "../systems/ShopUI";
import {
  WhirlpoolEntryData,
  WhirlpoolInstanceData,
} from "../systems/WhirlpoolRenderer";
import {
  playSound,
  playRandomCraftSound,
  playRandomSanctumCombatSound,
  playRandomCannonFireSound,
  playRandomCannonHitSound,
  playRandomCannonCritSound,
  playRandomCannonMissSound,
  playRandomCoinsGainSound,
  getSoundManager,
  SFX_KEYS,
} from "../systems/SoundManager";
import CacheInvalidation from "../../utils/CacheInvalidation";

export interface SocketHandlerCallbacks {
  // Ship handlers
  updateShips: (ships: ShipData[]) => void;
  createShipSprite: (ship: ShipData) => void;
  removeShip: (shipId: string) => void;
  getShips: () => Map<string, any>;
  getLocalShip: () => any | null;
  getLocalPlayerId: () => string;
  setLocalShip: (ship: any) => void;

  // Fishing spot handlers
  removeFishingSpot?: (spotId: string) => void;
  setFishingSpotInfo?: (spotType: string, spotId: string) => void;

  // Combat handlers
  showCannonEffect: (data: any) => void;
  showLootGainedEffect: (
    pieces: number,
    bounty: number,
    xp: number,
    newLevel?: number,
  ) => void;
  handlePlayerDeath: (killerName: string) => void;
  showShipSinkingEffect: (ship: any, shipName: string, pieces: number) => void;
  disengageTarget: () => void;
  hideTargetInfo: () => void;
  clearPathVisualization: (isCombat?: boolean) => void;
  clearAllPathVisualizations: () => void;
  getSelectedTarget: () => string | null;
  setSelectedTarget: (target: string | null) => void;
  renderEscapeSmokeEffect: (x: number, y: number) => void;

  // Path handlers
  showPathVisualization: (
    path: Array<{ tileX: number; tileY: number }>,
    targetTile: { tileX: number; tileY: number } | null,
    isCombat: boolean,
  ) => void;
  showPathFailedIndicator: (
    targetTile: { tileX: number; tileY: number },
    reason: string,
  ) => void;
  validateServerPath: (
    path: Array<{ tileX: number; tileY: number }>,
    targetTile: { tileX: number; tileY: number } | null,
  ) => boolean;
  stopMovement: () => void;
  isTileBlockedByLoadedIslands: (tileX: number, tileY: number) => boolean;
  isDoingWASDMovement?: () => boolean;

  // NPC handlers
  updateNPCs: (npcs: NPCData[]) => void;
  updateAmbientNPCs: (npcs: NPCData[]) => void;
  updateTradeRoutes: (routes: TradeRouteData[]) => void;
  showBossTelegraph: (data: any) => void;
  hideAmbientTradePanel: () => void;
  hideAmbientNPCTooltip: () => void;
  getAmbientNPCs: () => Map<string, any>;

  // Fishing handlers
  showFishingStatus: (message: string, isActive: boolean) => void;
  showFishingMessage: (message: string, color: string) => void;
  showFishingCatch: (fish: any, message: string, color: string) => void;
  showFishingError: (message: string) => void;
  setIsFishing: (isFishing: boolean) => void;
  startFishingMinigame: () => void;

  // Shop handlers
  displayShopUI: (shop: ShopData) => void;
  showPurchaseFeedback: (cost: number) => void;

  // Whirlpool handlers
  getWhirlpoolRenderer: () => any;
  setIsInWhirlpoolInstance: (isIn: boolean) => void;
  getIslandRenderer: () => any;
  getFishingSpotRenderer: () => any;
  getPlacedTileRenderer: () => any;
  getCameras: () => Phaser.Cameras.Scene2D.CameraManager;
  getRiftScarManager: () => any;
  setIsInRiftInstance: (isIn: boolean) => void;
  setCurrentRiftId: (riftId: string) => void;
  setInRift: (isIn: boolean) => void;
  setRiftMetadata: (metadata: any) => void;
  setInSanctum: (isIn: boolean) => void;
  setCurrentSanctumId: (id: string | null) => void;
  setSanctumMetadata: (metadata: any) => void;

  // UI handlers
  showFloatingMessage: (message: string, color: string) => void;
  showTreasureMarker: (
    id: string,
    x: number,
    y: number,
    reward: number,
    expiresIn: number,
  ) => void;
  removeTreasureMarker: (id: string) => void;
  showTreasureCollectedNotification: (reward: number, loot: any[]) => void;
  spawnFloatingBottle: (id: string, x: number, y: number) => void;
  removeFloatingBottle: (id: string) => void;
  showCraftingReadyToast: (recipeName: string, quantity: number) => void;
  setWeather: (weather: string, effects: any) => void;
  showGlobalBroadcast: (
    message: string,
    type: string,
    duration: number,
  ) => void;
  showChatMessage: (
    senderId: string,
    senderName: string,
    shipColor: string | number | undefined,
    message: string,
    position?: { x: number; y: number },
  ) => void;

  // Rift storm handlers
  spawnRiftStorm: (data: any) => void;
  removeRiftStorm: (id: string) => void;
  setRiftStormDissipating: (id: string, endsAt: number) => void;
  setCurrentRiftStormBonus: (
    bonus: { loot: number; xp: number } | null,
  ) => void;
  startStormAmbient: () => void;
  stopStormAmbient: () => void;
  getRiftStorms: () => Map<string, any>;

  // Sea monster handlers
  getSeaMonsterRenderer: () => any;

  // Shipwreck handlers
  spawnShipwreck: (
    id: string,
    x: number,
    y: number,
    size: string,
    rotation: number,
    lootQuality: string,
  ) => void;
  showSalvageUI: (lootQuality: string) => void;
  updateSalvageUI: (progress: number) => void;
  hideSalvageUI: () => void;
  showSalvageReward: (pieces: number, loot: any[], quality: string) => void;
  markShipwreckDepleted: (wreckId: string) => void;
  removeShipwreck: (wreckId: string) => void;
  setIsSalvaging: (isSalvaging: boolean) => void;

  // Resource node handlers
  spawnResourceNode: (node: any) => void;
  removeResourceNode: (nodeId: string) => void;
  hideHarvestUI: () => void;
  getResourceNodes: () => Map<string, any>;
  setIsHarvesting: (isHarvesting: boolean) => void;
  setCurrentHarvestNodeId: (nodeId: string | null) => void;
  getCurrentHarvestNodeId: () => string | null;

  // Loot container handlers
  spawnLootContainerMarker: (
    id: string,
    x: number,
    y: number,
    isPrivate: boolean,
  ) => void;
  removeLootContainerMarker: (id: string) => void;

  // Wildcard power handlers
  showWildcardPowerVisual: (
    casterX: number,
    casterY: number,
    targetX: number,
    targetY: number,
    color: string,
    aoeRadius: number,
    element: string,
  ) => void;
  getShipRenderer: () => any;

  // Docking handlers
  updateIslandOwnership: (islandId: string, data: any) => void;
  getPermanentLocationRenderer: () => any;

  // State getters
  isPlayerDead: () => boolean;
  getSocket: () => Socket | null;
  setCachedIslandCollisionCheck: (cache: any) => void;
  setLastFireTimestamp: (timestamp: number) => void;
  setIsSprinting: (isSprinting: boolean) => void;
  getIsSprinting: () => boolean;
}

export class SocketHandlerManager {
  private scene: Phaser.Scene;
  private callbacks: SocketHandlerCallbacks;
  private socket: Socket | null = null;
  private currentFishingSpotType: string = "shallow_waters";
  private currentFishingSpotId: string = "";

  // Update batching to reduce frame spikes
  private shipUpdateQueue: ShipData[] = [];
  private npcUpdateQueue: NPCData[] = [];
  private ambientNPCUpdateQueue: NPCData[] = [];
  private tradeRouteUpdateQueue: TradeRouteData[] = [];
  private maxUpdatesPerFrame = 20; // Process max 20 updates per frame
  private batchProcessingEnabled = true;
  private lastBatchTime = 0;
  private batchThrottleMs = 16; // Only process batches every 16ms (1 frame)

  constructor(scene: Phaser.Scene, callbacks: SocketHandlerCallbacks) {
    this.scene = scene;
    this.callbacks = callbacks;
    this.setupBatchProcessing();
  }

  private setupBatchProcessing(): void {
    // Process queued updates every frame to spread load
    this.scene.events.on("update", () => {
      if (!this.batchProcessingEnabled) return;

      // Process ship updates immediately (highest priority - affects gameplay)
      // Don't throttle ship updates as they need to be responsive
      if (this.shipUpdateQueue.length > 0) {
        const batch = this.shipUpdateQueue.splice(0, this.maxUpdatesPerFrame);
        this.callbacks.updateShips(batch);
      }

      const now = Date.now();
      // Throttle lower-priority updates to avoid frame spikes
      if (now - this.lastBatchTime < this.batchThrottleMs) return;
      this.lastBatchTime = now;

      // Process NPC updates
      if (this.npcUpdateQueue.length > 0) {
        const batch = this.npcUpdateQueue.splice(0, this.maxUpdatesPerFrame);
        this.callbacks.updateNPCs(batch);
      }

      // Process ambient NPC updates (lower priority)
      if (this.ambientNPCUpdateQueue.length > 0) {
        const batch = this.ambientNPCUpdateQueue.splice(
          0,
          Math.floor(this.maxUpdatesPerFrame / 2),
        );
        this.callbacks.updateAmbientNPCs(batch);
      }

      // Process trade route updates (lowest priority)
      if (this.tradeRouteUpdateQueue.length > 0) {
        const batch = this.tradeRouteUpdateQueue.splice(
          0,
          Math.floor(this.maxUpdatesPerFrame / 2),
        );
        this.callbacks.updateTradeRoutes(batch);
      }
    });
  }

  setSocket(socket: Socket | null): void {
    this.socket = socket;
    if (socket) {
      this.setupSocketListeners();
    }
  }

  private setupSocketListeners(): void {
    if (!this.socket) return;

    console.log(`🔌 SocketHandlerManager: Setting up socket listeners`);

    this.setupConnectionHandlers();
    this.setupShipHandlers();
    this.setupCombatHandlers();
    this.setupNPCHandlers();
    this.setupFishingHandlers();
    this.setupWildcardPowerHandlers();
    this.setupPathHandlers();
    this.setupWhirlpoolHandlers();
    this.setupTreasureHandlers();
    this.setupAmbientNPCHandlers();
    this.setupCraftingHandlers();
    this.setupWeatherHandlers();
    this.setupRiftStormHandlers();
    this.setupSeaMonsterHandlers();
    this.setupRaidHandlers();
    this.setupShipwreckHandlers();
    this.setupResourceNodeHandlers();
    this.setupBroadcastHandlers();
    this.setupTerritoryHandlers();
    this.setupDockingHandlers();
    this.setupLootContainerHandlers();
    this.setupShopHandlers();
    this.setupInventoryAndPlayerHandlers();
    this.setupRiftHandlers();
    this.setupConstructionHandlers();

    // Request initial data
    this.socket.emit("whirlpool:getAll");
    this.socket.emit("riftStorm:getAll");
    this.socket.emit("seaMonster:getAll");
    this.socket.emit("raid:getAll");
    this.socket.emit("resource:getAll");
    this.socket.emit("lootcontainer:list");
  }

  private setupConnectionHandlers(): void {
    if (!this.socket) return;

    this.socket.on("ping_request", () => {
      this.socket?.emit("pong_response");
    });

    this.socket.on("worldEvent:nearby", () => {
      playSound(SFX_KEYS.DON);
    });

    // Admin teleport - instant position update
    this.socket.on(
      "admin:teleported",
      (data: { x: number; y: number; message: string }) => {
        const localShip = this.callbacks.getLocalShip();
        if (localShip) {
          // Instantly update position without animation
          localShip.x = data.x;
          localShip.y = data.y;

          // Update camera to follow instantly
          const cameras = this.callbacks.getCameras();
          cameras.main.centerOn(data.x, data.y);

          // Admin teleports always place you in the overworld (core world)
          // so force-clear all instance flags that affect rendering and chunk loading.
          this.callbacks.setIsInWhirlpoolInstance(false);
          this.callbacks.setIsInRiftInstance(false);
          this.callbacks.setCurrentRiftId("core");

          // Emit scene events so React/UI + other systems follow the authoritative state.
          this.scene.events.emit("whirlpool:exited");
          this.scene.events.emit("rift:exited");
          this.scene.events.emit("sanctum:exited");

          // Reset rift/sanctum UI state
          this.callbacks.setInRift(false);
          this.callbacks.setRiftMetadata(null);
          this.callbacks.setInSanctum(false);
          this.callbacks.setCurrentSanctumId(null);
          this.callbacks.setSanctumMetadata(null);

          // Show message
          this.callbacks.showFloatingMessage(data.message, "#44ff44");
        }
      },
    );
  }

  private setupShipHandlers(): void {
    if (!this.socket) return;

    this.socket.on(
      "ships:update",
      (data: {
        ships: ShipData[];
        deltas?: Array<Partial<ShipData> & { id: string }>;
      }) => {
        // Deduplicate at source: keep only latest update for each ship
        const latestByShipId = new Map<string, ShipData>();

        // First, add existing queue items
        for (const ship of this.shipUpdateQueue) {
          latestByShipId.set(ship.id, ship);
        }

        // Process full ship syncs
        if (data.ships && data.ships.length > 0) {
          for (const ship of data.ships) {
            latestByShipId.set(ship.id, ship);
          }
        }

        // Process delta updates by merging with existing ship data
        if (data.deltas && data.deltas.length > 0) {
          const ships = this.callbacks.getShips();
          for (const delta of data.deltas) {
            // Get existing ship data from the scene or queue
            let existingShip = latestByShipId.get(delta.id);
            if (!existingShip) {
              const shipSprite = ships.get(delta.id);
              existingShip = shipSprite?.shipData;
            }

            if (existingShip) {
              // Merge delta into existing ship data
              const mergedShip: ShipData = { ...existingShip, ...delta };
              latestByShipId.set(delta.id, mergedShip);
            } else {
              // If we don't have existing data, treat delta as full sync (shouldn't happen)
              console.warn(
                `⚠️ Received delta for unknown ship ${delta.id}, treating as full sync`,
              );
              latestByShipId.set(delta.id, delta as ShipData);
            }
          }
        }

        // Replace queue with deduplicated version
        this.shipUpdateQueue = Array.from(latestByShipId.values());
      },
    );

    this.socket.on("sprint:error", (data: { message: string }) => {
      console.log(`🏃 [SPRINT] Error: ${data.message}`);
      this.callbacks.setIsSprinting(false);
      this.callbacks.showFloatingMessage(
        `Cannot sprint: ${data.message}`,
        "#ff6666",
      );
    });

    this.socket.on("ship:spawn", (data: { ship: ShipData }) => {
      const ships = this.callbacks.getShips();
      if (!ships.has(data.ship.id)) {
        this.callbacks.createShipSprite(data.ship);
      }

      if (data.ship.ownerId === this.callbacks.getLocalPlayerId()) {
        const newShip = ships.get(data.ship.id);
        if (newShip) {
          this.callbacks.setLocalShip(newShip);
          this.callbacks.getCameras().main.centerOn(newShip.x, newShip.y);
        }
      }
    });

    this.socket.on("ship:destroyed", (data: { shipId: string }) => {
      this.callbacks.removeShip(data.shipId);
    });

    this.socket.on(
      "player:flagChanged",
      (data: { characterId: string; playerName: string; expression: any }) => {
        const ships = this.callbacks.getShips();

        // Find the ship with this character ID
        for (const [, ship] of ships.entries()) {
          if (ship.shipData?.characterId === data.characterId) {
            if (ship.shipData) {
              ship.shipData.flagPattern = data.expression.flagPattern;
              ship.shipData.flagPrimaryColor = data.expression.flagPrimaryColor;
              ship.shipData.flagSecondaryColor =
                data.expression.flagSecondaryColor;
              ship.shipData.flagEmblem = data.expression.flagEmblem;
              ship.shipData.flagEmblemColor = data.expression.flagEmblemColor;
              console.log(`🚩 Flag updated for ${data.playerName}`);
              this.callbacks.updateShips([ship.shipData]);
            }
            break;
          }
        }
      },
    );

    this.socket.on(
      "player:death",
      (data: { shipId: string; killerName: string }) => {
        console.log(
          `💀 player:death event received! Killed by ${data.killerName}`,
        );
        this.callbacks.handlePlayerDeath(data.killerName);
      },
    );

    this.socket.on(
      "ship:sinking",
      (data: {
        shipId: string;
        shipName: string;
        killerName: string;
        position?: { x: number; y: number };
        loot?: { pieces: number };
      }) => {
        console.log(
          `🚢 ship:sinking event: ${data.shipName} sunk by ${data.killerName}`,
        );

        const ships = this.callbacks.getShips();
        const sinkingShip = ships.get(data.shipId);
        if (sinkingShip) {
          this.callbacks.showShipSinkingEffect(
            sinkingShip,
            data.shipName,
            data.loot?.pieces || 0,
          );
          playSound(SFX_KEYS.SHIP_EXPLOSION);
          playSound(SFX_KEYS.SHIP_SINKING);
        }

        if (this.callbacks.getSelectedTarget() === data.shipId) {
          console.log(`⚔️ Combat target sunk, disengaging`);
          this.callbacks.disengageTarget();
        }

        const localShip = this.callbacks.getLocalShip();
        if (localShip && localShip.shipId === data.shipId) {
          console.log(`💀 Our ship is sinking! Triggering death handler...`);
          this.callbacks.handlePlayerDeath(data.killerName);
        }
      },
    );
  }

  private setupCombatHandlers(): void {
    if (!this.socket) return;

    this.socket.on(
      "combat:started",
      (data: {
        targetId: string;
        targetName: string;
        targetFaction: string;
        targetLevel: number;
      }) => {
        console.log(
          `⚔️ Combat started with ${data.targetName} (faction: ${data.targetFaction}, level: ${data.targetLevel})`,
        );
        // Combat has begun - the minigame can now be triggered via SPACE key
        // The cannon focus UI will show the combat prompt
      },
    );

    this.socket.on(
      "combat:cannon_fire",
      (data: {
        attackerId: string;
        targetId: string;
        result: { hit: boolean; damage: number; isCrit: boolean };
        weaponRarity?: string;
      }) => {
        const localShip = this.callbacks.getLocalShip();
        const ships = this.callbacks.getShips();

        const isLocalPlayerInvolved =
          localShip &&
          (data.attackerId === localShip.shipId ||
            data.targetId === localShip.shipId);

        const attackerVisible = ships.has(data.attackerId);
        const targetVisible = ships.has(data.targetId);

        if (!isLocalPlayerInvolved && !attackerVisible && !targetVisible) {
          return;
        }

        console.log(
          `💥 Cannon fire: ${data.attackerId} -> ${data.targetId}, hit: ${data.result.hit}, damage: ${data.result.damage}`,
        );
        this.callbacks.showCannonEffect(data);

        // Play sounds with distance attenuation to avoid hearing off-screen / far-away combat
        // Prefer attacker position (source of cannon fire); fall back to target position.
        if (localShip) {
          const attacker = ships.get(data.attackerId);
          const target = ships.get(data.targetId);
          const source = attacker ?? target;

          const isInSanctum: boolean = Boolean(
            localShip.shipData?.sanctumInstanceId,
          );
          const attackerIsNpc: boolean = Boolean(
            attacker?.shipData?.ownerType === "npc",
          );
          const targetIsNpc: boolean = Boolean(
            target?.shipData?.ownerType === "npc",
          );
          const shouldUseSanctumCombatSfx: boolean =
            isInSanctum &&
            isLocalPlayerInvolved &&
            (attackerIsNpc || targetIsNpc);

          if (source) {
            const sourcePosition = {
              x: source.x as number,
              y: source.y as number,
            };
            const listenerPosition = {
              x: localShip.x as number,
              y: localShip.y as number,
            };

            if (shouldUseSanctumCombatSfx) {
              playRandomSanctumCombatSound(sourcePosition, listenerPosition);
            } else {
              const maxDistancePx = 900;
              playRandomCannonFireSound(
                sourcePosition,
                listenerPosition,
                0.6,
                maxDistancePx,
              );
              if (data.result.hit) {
                if (data.result.isCrit) {
                  playRandomCannonCritSound(
                    sourcePosition,
                    listenerPosition,
                    0.7,
                    maxDistancePx,
                  );
                } else {
                  playRandomCannonHitSound(
                    sourcePosition,
                    listenerPosition,
                    0.5,
                    maxDistancePx,
                  );
                }
              } else {
                playRandomCannonMissSound(
                  sourcePosition,
                  listenerPosition,
                  0.3,
                  maxDistancePx,
                );
              }
            }
          } else {
            // If we can't resolve a world position, fall back to non-spatial sound only
            // when the local player is directly involved.
            if (isLocalPlayerInvolved) {
              const soundManager = getSoundManager();
              if (soundManager) {
                const isInSanctum: boolean = Boolean(
                  localShip.shipData?.sanctumInstanceId,
                );
                const attackerIsNpc: boolean = Boolean(
                  attacker?.shipData?.ownerType === "npc",
                );
                const targetIsNpc: boolean = Boolean(
                  target?.shipData?.ownerType === "npc",
                );
                const shouldUseSanctumCombatSfx: boolean =
                  isInSanctum && (attackerIsNpc || targetIsNpc);
                if (shouldUseSanctumCombatSfx) {
                  soundManager.play(SFX_KEYS.SANCTUM_COMBAT_1, 0.55);
                } else {
                  soundManager.play(SFX_KEYS.CANNON_FIRE_1, 0.6);
                  if (data.result.hit) {
                    if (data.result.isCrit) {
                      soundManager.play(SFX_KEYS.CANNON_CRIT_1, 0.7);
                    } else {
                      soundManager.play(SFX_KEYS.CANNON_HIT_1, 0.5);
                    }
                  } else {
                    soundManager.play(SFX_KEYS.CANNON_MISS_WATER_1, 0.3);
                  }
                }
              }
            }
          }
        }

        if (localShip && data.attackerId === localShip.shipId) {
          this.callbacks.setLastFireTimestamp(Date.now());
        }
      },
    );

    this.socket.on(
      "loot:gained",
      (data: {
        pieces: number;
        xp: number;
        source: string;
        bounty: number;
        newLevel?: number;
      }) => {
        console.log(
          `💰 Loot gained: ${data.pieces} pieces + ${data.xp} XP from ${data.source}`,
        );
        this.callbacks.showLootGainedEffect(
          data.pieces,
          data.bounty,
          data.xp,
          data.newLevel,
        );

        if (data.pieces > 0 || data.bounty > 0) {
          playRandomCoinsGainSound();
        }
        if (data.xp > 0) {
          playSound(SFX_KEYS.XP_GAIN);
        }
        if (data.newLevel) {
          playSound(SFX_KEYS.LEVEL_UP);
        }
      },
    );

    this.socket.on(
      "combat:ended",
      (data: { shipId: string; previousTargetId: string | null }) => {
        console.log(`⚔️ Combat ended for ${data.shipId}, clearing visuals`);

        const localShip = this.callbacks.getLocalShip();
        if (
          this.callbacks.getSelectedTarget() === data.previousTargetId ||
          (localShip && localShip.shipId === data.shipId)
        ) {
          this.callbacks.setSelectedTarget(null);
          this.callbacks.hideTargetInfo();
          this.callbacks.clearPathVisualization(true);
          this.scene.events.emit("cannonFocus:deactivate");
        }
      },
    );

    this.socket.on(
      "combat:escaped",
      (data: { message: string; x: number; y: number; playerId: string }) => {
        console.log(`💨 ${data.message}`);
        this.callbacks.renderEscapeSmokeEffect(data.x, data.y);
      },
    );
  }

  private setupNPCHandlers(): void {
    if (!this.socket) return;

    this.socket.on("npc:updates", (data: NPCData[]) => {
      // Queue updates instead of processing immediately to reduce frame spikes
      this.npcUpdateQueue.push(...data);
    });

    this.socket.on("ambient:update", (data: { npcs: NPCData[] }) => {
      // Queue updates instead of processing immediately to reduce frame spikes
      this.ambientNPCUpdateQueue.push(...data.npcs);
    });

    this.socket.on(
      "tradeRoutes:update",
      (data: { visibleRoutes: TradeRouteData[] }) => {
        // Queue updates instead of processing immediately to reduce frame spikes
        this.tradeRouteUpdateQueue.push(...data.visibleRoutes);
      },
    );

    this.socket.on("boss:telegraph", (data: any) => {
      this.callbacks.showBossTelegraph(data);
    });
  }

  private setupFishingHandlers(): void {
    if (!this.socket) return;

    this.socket.on(
      "fishing:started",
      (data: { spotId: string; spotType: string; message: string }) => {
        console.log(`🎣 ${data.message}`);
        this.callbacks.showFishingStatus(data.message, true);
        this.currentFishingSpotType = data.spotType;
        this.currentFishingSpotId = data.spotId;
        // Set fishing spot info before setting fishing state (so minigame can use it)
        this.callbacks.setFishingSpotInfo?.(data.spotType, data.spotId);
        this.callbacks.setIsFishing(true);
        playSound(SFX_KEYS.FISHING_CAST);
      },
    );

    this.socket.on("fishing:stopped", (data: { message: string }) => {
      console.log(`🎣 ${data.message}`);
      this.callbacks.showFishingStatus(data.message, false);
      this.callbacks.setIsFishing(false);
      // Emit event to deactivate the React fishing minigame component
      this.scene?.events.emit("fishing:minigame:deactivate");
    });

    this.socket.on("fishing:noCatch", (data: { message: string }) => {
      this.callbacks.showFishingMessage(data.message, "#aaaaaa");
      playSound(SFX_KEYS.FISHING_NO_CATCH);
    });

    this.socket.on(
      "fishing:caught",
      (data: {
        fish: any;
        message: string;
        color: string;
        minigameChance?: number;
      }) => {
        console.log(`🐟 ${data.message}`);
        this.callbacks.showFishingCatch(data.fish, data.message, data.color);
        if (
          data.fish.category === "legendary" ||
          data.fish.category === "rare"
        ) {
          playSound(SFX_KEYS.FISHING_RARE_CATCH);
        } else {
          playSound(SFX_KEYS.FISHING_CATCH);
        }

        // 70% chance to trigger minigame after catch for better engagement and pacing
        if (
          data.minigameChance !== undefined &&
          Math.random() < data.minigameChance
        ) {
          // Delay minigame start slightly so catch notification displays first
          setTimeout(() => {
            this.callbacks.startFishingMinigame();
          }, 800);
        }
      },
    );

    this.socket.on("fishing:error", (data: { message: string }) => {
      console.log(`❌ Fishing error: ${data.message}`);
      this.callbacks.showFishingError(data.message);
      this.callbacks.setIsFishing(false);
      // Emit event to deactivate the React fishing minigame component
      this.scene?.events.emit("fishing:minigame:deactivate");
      this.socket?.emit("fishing:stop");
    });

    this.socket.on(
      "fishing:spotDepleted",
      (data: { message: string; spotId: string }) => {
        console.log(`🎣 ${data.message}`);
        this.callbacks.showFishingMessage(data.message, "#ff6b6b");
        this.callbacks.setIsFishing(false);
        // Remove the depleted fishing spot from the renderer
        this.callbacks.removeFishingSpot?.(data.spotId);
        // Emit event to deactivate the React fishing minigame component
        this.scene?.events.emit("fishing:minigame:deactivate");
      },
    );
  }

  private setupWildcardPowerHandlers(): void {
    if (!this.socket) return;

    this.socket.on("wildcard:activated", (data: any) => {
      // Handle null/undefined customAlias - use definition's baseName as fallback
      const customAlias = data.power?.customAlias;
      const powerName =
        customAlias && customAlias !== "null"
          ? customAlias
          : data.definition?.baseName || "Wildcard Power";
      console.log(`⚡ Wildcard power activated: ${powerName}`);
      this.callbacks.showFloatingMessage(
        `${powerName}!`,
        data.definition?.color || "#4fc3f7",
      );
      playSound(SFX_KEYS.WILDCARD_ACTIVATE);
      this.scene.events.emit("wildcard:activated", data);
    });

    this.socket.on("wildcard:error", (data: { message: string }) => {
      console.log(`❌ Wildcard error: ${data.message}`);
      if (data.message !== "No wildcard power equipped") {
        this.callbacks.showFloatingMessage(data.message, "#ff4444");
      }
    });

    this.socket.on("wildcard:evolved", (data: any) => {
      console.log(`⚡ Wildcard power evolved`);
      this.callbacks.showFloatingMessage(
        `${data.oldPower.customAlias} EVOLVED!`,
        "#ffd700",
      );
      playSound(SFX_KEYS.WILDCARD_EVOLVE);
      this.scene.events.emit("wildcard:evolved", data);
    });

    this.socket.on(
      "wildcard:visual",
      (data: {
        playerId: string;
        shipId: string;
        casterX: number;
        casterY: number;
        x: number;
        y: number;
        element: string;
        color: string;
        aoeRadius: number;
      }) => {
        this.callbacks.showWildcardPowerVisual(
          data.casterX,
          data.casterY,
          data.x,
          data.y,
          data.color,
          data.aoeRadius,
          data.element,
        );
      },
    );

    this.socket.on(
      "wildcard:hit",
      (data: {
        attackerId: string;
        targetId: string;
        damage: number;
        powerId: string;
        element: string;
      }) => {
        const ships = this.callbacks.getShipRenderer().getShips();
        const target = ships.get(data.targetId);
        if (target) {
          const text = this.scene.add.text(
            target.x,
            target.y - 30,
            `-${data.damage}`,
            {
              fontSize: "18px",
              color: "#ff4444",
              fontFamily: "Sometype Mono, monospace",
              stroke: "#000000",
              strokeThickness: 3,
            },
          );
          text.setOrigin(0.5);
          text.setDepth(100);

          (this.scene as any).tweens.add({
            targets: text,
            y: target.y - 60,
            alpha: 0,
            duration: 1000,
            ease: "Quad.easeOut",
            onComplete: () => text.destroy(),
          });
        }
      },
    );

    this.socket.on("wildcard:unlocked", (data: any) => {
      console.log(`⚡ New wildcard power unlocked: ${data.power.customAlias}`);
      this.callbacks.showFloatingMessage(
        `NEW WILDCARD: ${data.power.customAlias}`,
        data.definition.color,
      );
      this.scene.events.emit("wildcard:unlocked", data);
    });

    // Admin testing: test cannon fire effect
    this.socket.on(
      "admin:testCannonFireEffect",
      (data: {
        shipId: string;
        ownerName: string;
        x: number;
        y: number;
        weaponRarity: string;
        signatureElement?: string | null;
        powerLevel: number;
        aoeRadius: number;
        intensityMultiplier: number;
        timestamp: number;
      }) => {
        console.log(
          `🧪 Admin test fire: ${data.weaponRarity}${data.signatureElement ? ` + ${data.signatureElement}` : ""} (${data.intensityMultiplier}x intensity)`,
        );

        // Show visual effect at the test location
        const colors = {
          flame: "#ff6b35",
          storm: "#00bfff",
          tide: "#1e90ff",
          shadow: "#4b0082",
          iron: "#c0c0c0",
          spirit: "#9932cc",
        };

        const color =
          data.signatureElement &&
          colors[data.signatureElement as keyof typeof colors]
            ? colors[data.signatureElement as keyof typeof colors]
            : "#ffffff";

        this.callbacks.showWildcardPowerVisual(
          data.x,
          data.y,
          data.x,
          data.y,
          color,
          data.aoeRadius,
          data.signatureElement || "test",
        );

        // Show info message
        const info = `Test: ${data.weaponRarity}${data.signatureElement ? ` + ${data.signatureElement}` : ""}\nIntensity: ${data.intensityMultiplier.toFixed(1)}x | AOE: ${data.aoeRadius}`;
        this.callbacks.showFloatingMessage(info, color);
      },
    );
  }

  private setupPathHandlers(): void {
    if (!this.socket) return;

    this.socket.on(
      "ship:path",
      (data: {
        shipId: string;
        path: Array<{ tileX: number; tileY: number }>;
        targetTile: { tileX: number; tileY: number } | null;
        isCombat?: boolean;
      }) => {
        if (this.callbacks.isPlayerDead()) return;
        if (this.callbacks.getSelectedTarget()) return;

        // Suppress path visualization during WASD movement (direct movement doesn't need visual feedback)
        const isWASDMoving = this.callbacks.isDoingWASDMovement?.() ?? false;
        if (isWASDMoving) return;

        const localShip = this.callbacks.getLocalShip();
        const isOurShip =
          data.shipId.includes(this.callbacks.getLocalPlayerId()) ||
          (localShip && localShip.shipId === data.shipId);

        if (isOurShip) {
          if (data.isCombat) return;

          const isValid = this.callbacks.validateServerPath(
            data.path,
            data.targetTile,
          );
          if (!isValid) {
            console.warn("Ignored invalid ship:path payload from server");
            if (data.targetTile) {
              this.callbacks.showPathFailedIndicator(
                data.targetTile,
                "blocked",
              );
            }
            this.callbacks.clearPathVisualization(false);
            this.callbacks.stopMovement();
            return;
          }

          this.callbacks.showPathVisualization(
            data.path,
            data.targetTile,
            false,
          );
        }
      },
    );

    this.socket.on(
      "ship:path_failed",
      (data: {
        shipId: string;
        targetTile: { tileX: number; tileY: number };
        reason: string;
      }) => {
        const localShip = this.callbacks.getLocalShip();
        const isOurShip =
          data.shipId.includes(this.callbacks.getLocalPlayerId()) ||
          (localShip && localShip.shipId === data.shipId);

        // Suppress path failed indicator during WASD movement (direct movement doesn't need validation feedback)
        const isWASDMoving = this.callbacks.isDoingWASDMovement?.() ?? false;

        if (isOurShip && !this.callbacks.getSelectedTarget() && !isWASDMoving) {
          console.log(
            `❌ Path failed to (${data.targetTile.tileX}, ${data.targetTile.tileY}): ${data.reason}`,
          );
          this.callbacks.showPathFailedIndicator(data.targetTile, data.reason);
        }
      },
    );

    this.socket.on(
      "ship:position_corrected",
      (data: {
        shipId: string;
        oldPosition: { x: number; y: number };
        newPosition: { x: number; y: number };
        reason: string;
      }) => {
        console.warn(`🚨 Position corrected by server: ${data.reason}`);
        this.callbacks.showFloatingMessage(
          "Position corrected - collision detected",
          "#ffaa00",
        );
      },
    );

    this.socket.on(
      "collision:correction",
      (data: { tileX: number; tileY: number; isBlocked: boolean }) => {
        console.log(
          `🔍 Collision correction received: (${data.tileX},${data.tileY}) isBlocked=${data.isBlocked}`,
        );
        this.callbacks.setCachedIslandCollisionCheck(null);
      },
    );
  }

  private setupWhirlpoolHandlers(): void {
    if (!this.socket) return;

    this.socket.on("whirlpool:list", (entries: WhirlpoolEntryData[]) => {
      console.log(`🌀 Received ${entries.length} whirlpools`);
      const renderer = this.callbacks.getWhirlpoolRenderer();
      if (renderer) {
        renderer.renderWhirlpools(entries);
      }
    });

    this.socket.on("whirlpool:spawned", (entry: WhirlpoolEntryData) => {
      console.log(`🌀 New whirlpool spawned: ${entry.class}`);
      const renderer = this.callbacks.getWhirlpoolRenderer();
      if (renderer) {
        renderer.renderWhirlpools([entry]);
      }
    });

    this.socket.on("whirlpool:despawned", (data: { id: string }) => {
      console.log(`🌀 Whirlpool despawned: ${data.id}`);
      this.socket?.emit("whirlpool:getAll");
    });

    this.socket.on(
      "whirlpool:entered",
      (data: { entryId: string; instance: WhirlpoolInstanceData }) => {
        console.log(
          `🌀 Entered whirlpool instance: ${data.instance.className}`,
        );
        this.callbacks.setIsInWhirlpoolInstance(true);

        const islandRenderer = this.callbacks.getIslandRenderer();
        islandRenderer.getIslands().forEach((entry: any) => {
          entry.graphics.setVisible(false);
          entry.hitArea?.setVisible(false);
        });

        const fishingRenderer = this.callbacks.getFishingSpotRenderer();
        fishingRenderer.getSpots().forEach((spot: any) => {
          spot.graphics.setVisible(false);
          spot.hitArea.setVisible(false);
        });

        this.callbacks
          .getCameras()
          .main.setBounds(0, 0, data.instance.width, data.instance.height);
        this.callbacks.getWhirlpoolRenderer().enterInstance(data.instance);

        this.scene.events.emit("whirlpool:entered", {
          instance: data.instance,
        });
        this.callbacks.showFloatingMessage(
          `Entered ${data.instance.className}`,
          "#00ffff",
        );
      },
    );

    this.socket.on(
      "whirlpool:exited",
      (_data?: { exitPosition?: { x: number; y: number } }) => {
        console.log(`🌀 Exited whirlpool`);
        this.callbacks.setIsInWhirlpoolInstance(false);
        this.callbacks.getWhirlpoolRenderer().exitInstance();

        const islandRenderer = this.callbacks.getIslandRenderer();
        islandRenderer.getIslands().forEach((entry: any) => {
          entry.graphics.setVisible(true);
          entry.hitArea?.setVisible(true);
        });

        const fishingRenderer = this.callbacks.getFishingSpotRenderer();
        fishingRenderer.getSpots().forEach((spot: any) => {
          spot.graphics.setVisible(true);
          spot.hitArea.setVisible(true);
        });

        this.scene.events.emit("whirlpool:exited");
        this.socket?.emit("whirlpool:getAll");
        this.callbacks.showFloatingMessage("Returned to the ocean", "#00ffff");
      },
    );

    // Additional whirlpool handlers...
    this.socket.on(
      "whirlpool:resourceHarvested",
      (data: {
        resourceId: string;
        loot: { type: string; amount: number };
        remainingAmount: number;
        inventoryResult?: { success: boolean; message: string };
      }) => {
        if (data.inventoryResult && data.inventoryResult.success === false) {
          this.callbacks.showFloatingMessage(
            data.inventoryResult.message,
            "#ff4444",
          );
          return;
        }
        console.log(`🌀 Harvested ${data.loot.amount}x ${data.loot.type}`);
        this.callbacks
          .getWhirlpoolRenderer()
          .updateResource(data.resourceId, data.remainingAmount);
        this.callbacks.showFloatingMessage(
          `+${data.loot.amount} ${data.loot.type}`,
          "#00ff88",
        );
      },
    );

    this.socket.on("whirlpool:error", (data: { message: string }) => {
      console.log(`🌀 Whirlpool error: ${data.message}`);
      this.callbacks.showFloatingMessage(data.message, "#ff4444");
    });
  }

  private setupTreasureHandlers(): void {
    if (!this.socket) return;

    this.socket.on(
      "treasure:revealed",
      (data: {
        id: string;
        x: number;
        y: number;
        reward: number;
        expiresIn: number;
      }) => {
        console.log(`[TREASURE] Treasure revealed at (${data.x}, ${data.y})`);
        this.callbacks.showTreasureMarker(
          data.id,
          data.x,
          data.y,
          data.reward,
          data.expiresIn,
        );
        this.scene.game.events.emit("treasure:destination", {
          x: data.x,
          y: data.y,
          name: "Treasure Location",
        });
      },
    );

    this.socket.on(
      "treasure:collected",
      (data: { id: string; reward: number; loot: any[] }) => {
        console.log(`[TREASURE] Treasure collected! Reward: ${data.reward}`);
        this.callbacks.removeTreasureMarker(data.id);
        this.callbacks.showTreasureCollectedNotification(
          data.reward,
          data.loot,
        );
      },
    );

    this.socket.on("treasure:expired", (data: { id: string }) => {
      console.log(`[TREASURE] Treasure expired: ${data.id}`);
      this.callbacks.removeTreasureMarker(data.id);
      this.callbacks.showFloatingMessage(
        "The treasure has vanished...",
        "#ff6666",
      );
    });

    this.socket.on(
      "bottle:spawned",
      (data: { id: string; x: number; y: number }) => {
        this.callbacks.spawnFloatingBottle(data.id, data.x, data.y);
      },
    );

    this.socket.on(
      "bottle:collected",
      (data: { id: string; type: string; content: string; reward?: any }) => {
        this.callbacks.removeFloatingBottle(data.id);
        this.scene.game.events.emit("bottle:show", {
          type: data.type,
          content: data.content,
          reward: data.reward,
        });

        if (data.reward?.treasureX && data.reward?.treasureY) {
          this.callbacks.showTreasureMarker(
            `bottle_treasure_${Date.now()}`,
            data.reward.treasureX,
            data.reward.treasureY,
            data.reward.pieces || 100,
            300000,
          );
          this.scene.game.events.emit("treasure:destination", {
            x: data.reward.treasureX,
            y: data.reward.treasureY,
            name: "Treasure Location",
          });
        }
      },
    );

    this.socket.on("bottle:despawned", (data: { id: string }) => {
      this.callbacks.removeFloatingBottle(data.id);
    });
  }

  private setupAmbientNPCHandlers(): void {
    if (!this.socket) return;

    this.socket.on("ambient:tradeInventory", (data: any) => {
      this.callbacks.hideAmbientTradePanel();
      this.scene.events.emit("ambient:trade", data);
    });

    this.socket.on("ambient:tradeError", (data: { message: string }) => {
      this.callbacks.showFloatingMessage(data.message, "#ff6666");
    });

    this.socket.on("ambient:buySuccess", (data: { message: string }) => {
      this.callbacks.showFloatingMessage(data.message, "#66ff66");
    });

    this.socket.on(
      "ambient:attackResult",
      (data: { killed: boolean; infamyGained: number; message: string }) => {
        const color = data.killed ? "#ff4444" : "#ffaa44";
        this.callbacks.showFloatingMessage(
          `${data.message} (+${data.infamyGained} infamy)`,
          color,
        );
      },
    );

    this.socket.on("ambient:attackError", (data: { message: string }) => {
      this.callbacks.showFloatingMessage(data.message, "#ff6666");
    });

    this.socket.on("ambient:killed", (data: { id: string }) => {
      const ambientNPCs = this.callbacks.getAmbientNPCs();
      const npc = ambientNPCs.get(data.id);
      if (npc) {
        npc.destroy();
        ambientNPCs.delete(data.id);
      }
      this.callbacks.hideAmbientNPCTooltip();
    });
  }

  private setupCraftingHandlers(): void {
    if (!this.socket) return;

    this.socket.on(
      "crafting:ready",
      (data: {
        recipeName: string;
        resultItemId: string;
        resultQuantity: number;
      }) => {
        console.log(`[CRAFTING] Craft ready: ${data.recipeName}`);
        this.callbacks.showCraftingReadyToast(
          data.recipeName,
          data.resultQuantity,
        );
        playRandomCraftSound();
      },
    );

    this.socket.on("crafting:completed", (_data: { recipeName: string }) => {
      playRandomCraftSound();
    });
  }

  private setupWeatherHandlers(): void {
    if (!this.socket) return;

    this.socket.on(
      "weather:changed",
      (data: { weather: string; duration: number; effects: any }) => {
        console.log(`[WEATHER] Weather changed to ${data.weather}`);
        this.callbacks.setWeather(data.weather, data.effects);
      },
    );
  }

  private setupRiftStormHandlers(): void {
    if (!this.socket) return;

    this.socket.on("riftStorm:list", (storms: any[]) => {
      console.log(`⚡ Received ${storms.length} active rift storms`);

      const existingStorms = this.callbacks.getRiftStorms();
      const existingStormIds = Array.from(existingStorms.keys());
      for (const id of existingStormIds) {
        this.callbacks.removeRiftStorm(id);
      }

      const cappedStorms = storms.slice(0, 3);
      for (const storm of cappedStorms) {
        this.callbacks.spawnRiftStorm(storm);
        if (storm.stage === "dissipating" && storm.dissipatesAt) {
          this.callbacks.setRiftStormDissipating(storm.id, storm.dissipatesAt);
        }
      }
    });

    this.socket.on("riftStorm:spawned", (data: any) => {
      console.log(`⚡ Rift Storm spawned: ${data.tier}`);
      const riftStorms = this.callbacks.getRiftStorms();
      if (riftStorms.size >= 3 && !riftStorms.has(data.id)) {
        return;
      }
      this.callbacks.spawnRiftStorm(data);
      if (data.stage === "dissipating" && data.dissipatesAt) {
        this.callbacks.setRiftStormDissipating(data.id, data.dissipatesAt);
      }
    });

    this.socket.on(
      "riftStorm:dissipating",
      (data: { id: string; endsAt: number }) => {
        this.callbacks.setRiftStormDissipating(data.id, data.endsAt);
      },
    );

    this.socket.on("riftStorm:despawned", (data: { id: string }) => {
      console.log(`⚡ Rift Storm despawned: ${data.id}`);
      this.callbacks.removeRiftStorm(data.id);
    });

    this.socket.on(
      "riftStorm:entered",
      (data: {
        id: string;
        tier: string;
        lootMultiplier: number;
        xpMultiplier: number;
      }) => {
        console.log(`⚡ Entered ${data.tier} rift storm`);
        this.callbacks.setCurrentRiftStormBonus({
          loot: data.lootMultiplier,
          xp: data.xpMultiplier,
        });
        this.callbacks.showFloatingMessage(
          `ENTERED RIFT STORM - ${data.lootMultiplier}x LOOT`,
          "#ff6b35",
        );
        this.scene.events.emit("riftStorm:entered", data);
        this.callbacks.startStormAmbient();
      },
    );

    this.socket.on("riftStorm:exited", (data: { id: string }) => {
      console.log(`⚡ Exited rift storm`);
      this.callbacks.setCurrentRiftStormBonus(null);
      this.callbacks.showFloatingMessage("Exited Rift Storm", "#888888");
      this.scene.events.emit("riftStorm:exited", data);
      this.callbacks.stopStormAmbient();
    });
  }

  private setupSeaMonsterHandlers(): void {
    if (!this.socket) return;

    this.socket.on("seaMonster:list", (monsters: any[]) => {
      console.log(`🐙 Received ${monsters.length} active sea monsters`);
      const renderer = this.callbacks.getSeaMonsterRenderer();
      if (renderer) {
        for (const monster of monsters) {
          renderer.addMonster(monster);
        }
      }
    });

    this.socket.on("seaMonster:spawned", (data: any) => {
      console.log(`🐙 Sea Monster spawned: ${data.type}`);
      const renderer = this.callbacks.getSeaMonsterRenderer();
      if (renderer) {
        renderer.addMonster(data);
      }
    });

    this.socket.on("seaMonster:positions", (data: { monsters: any[] }) => {
      const renderer = this.callbacks.getSeaMonsterRenderer();
      if (renderer) {
        renderer.updatePositions(data.monsters);
      }
    });

    this.socket.on("seaMonster:despawned", (data: { id: string }) => {
      console.log(`🐙 Sea Monster departed: ${data.id}`);
      const renderer = this.callbacks.getSeaMonsterRenderer();
      if (renderer) {
        renderer.removeMonster(data.id);
      }
    });
  }

  private setupRaidHandlers(): void {
    if (!this.socket) return;

    this.socket.on("raid:list", (raids: any[]) => {
      console.log(`🏴‍☠️ Received ${raids.length} active raids`);
      this.scene.events.emit("raid:list", raids);
    });

    this.socket.on("raid:started", (data: any) => {
      console.log(`🏴‍☠️ Pirate Raid started at ${data.islandName}!`);
      this.scene.events.emit("raid:started", data);
    });

    this.socket.on("raid:progress", (data: any) => {
      this.scene.events.emit("raid:progress", data);
    });

    this.socket.on("raid:ended", (data: any) => {
      console.log(
        `🏴‍☠️ Raid ended at ${data.islandName}: ${data.success ? "SUCCESS" : "FAILED"}`,
      );
      this.scene.events.emit("raid:ended", data);
    });
  }

  private setupShipwreckHandlers(): void {
    if (!this.socket) return;

    this.socket.on(
      "shipwreck:spawned",
      (data: {
        id: string;
        x: number;
        y: number;
        size: string;
        rotation: number;
        lootQuality: string;
      }) => {
        this.callbacks.spawnShipwreck(
          data.id,
          data.x,
          data.y,
          data.size,
          data.rotation,
          data.lootQuality,
        );
      },
    );

    this.socket.on(
      "shipwreck:salvageStarted",
      (data: { wreckId: string; lootQuality: string; message: string }) => {
        console.log(`[SHIPWRECK] ${data.message}`);
        this.callbacks.setIsSalvaging(true);
        this.callbacks.showSalvageUI(data.lootQuality);
      },
    );

    this.socket.on(
      "shipwreck:progress",
      (data: { wreckId: string; progress: number }) => {
        this.callbacks.updateSalvageUI(data.progress);
      },
    );

    this.socket.on(
      "shipwreck:salvageComplete",
      (data: {
        wreckId: string;
        pieces: number;
        loot: any[];
        quality: string;
      }) => {
        console.log(`[SHIPWRECK] Salvage complete! ${data.pieces} pieces`);
        this.callbacks.setIsSalvaging(false);
        this.callbacks.hideSalvageUI();
        this.callbacks.showSalvageReward(data.pieces, data.loot, data.quality);
      },
    );

    this.socket.on("shipwreck:salvageStopped", () => {
      this.callbacks.setIsSalvaging(false);
      this.callbacks.hideSalvageUI();
    });

    this.socket.on("shipwreck:depleted", (data: { wreckId: string }) => {
      this.callbacks.markShipwreckDepleted(data.wreckId);
    });

    this.socket.on("shipwreck:removed", (data: { wreckId: string }) => {
      this.callbacks.removeShipwreck(data.wreckId);
    });

    this.socket.on(
      "shipwreck:error",
      (data: { message: string; wreckId?: string; notFound?: boolean }) => {
        this.callbacks.showFloatingMessage(data.message, "#ff6666");
        if (data.notFound && data.wreckId) {
          this.callbacks.removeShipwreck(data.wreckId);
        }
      },
    );
  }

  private setupResourceNodeHandlers(): void {
    if (!this.socket) return;

    this.socket.on("resource:list", (nodes: any) => {
      // Handle case where nodes might not be an array
      if (!Array.isArray(nodes)) {
        console.warn("🚨 resource:list received non-array data:", nodes);
        return;
      }

      const resourceNodes = this.callbacks.getResourceNodes();
      resourceNodes.forEach((node) => node.destroy());
      resourceNodes.clear();

      for (const node of nodes) {
        if (!node.isDepleted) {
          this.callbacks.spawnResourceNode(node);
        }
      }
    });

    this.socket.on("resource:spawned", (node: any) => {
      this.callbacks.spawnResourceNode(node);
    });

    this.socket.on("resource:respawned", (node: any) => {
      const resourceNodes = this.callbacks.getResourceNodes();
      if (!resourceNodes.has(node.id)) {
        this.callbacks.spawnResourceNode(node);
      }
    });

    this.socket.on(
      "resource:harvest_started",
      (data: { nodeId: string; harvestTime: number }) => {
        this.callbacks.setIsHarvesting(true);
        this.callbacks.setCurrentHarvestNodeId(data.nodeId);

        const resourceNodes = this.callbacks.getResourceNodes();
        const nodeContainer = resourceNodes.get(data.nodeId);
        const hitArea = nodeContainer ? (nodeContainer as any).hitArea : null;
        const nodeName = hitArea?.nodeName || "Resource";
        const requiredSkill = hitArea?.requiredSkill || undefined;
        const requiredLevel = hitArea?.requiredLevel || undefined;

        this.scene.events.emit("harvest:started", {
          nodeId: data.nodeId,
          harvestTime: data.harvestTime,
          nodeName,
          requiredSkill,
          requiredLevel,
        });
      },
    );

    this.socket.on(
      "resource:harvest_complete",
      (data: {
        nodeId: string;
        drops?: any[];
        xpGained?: number;
        bonusApplied?: string;
        skillType?: string;
      }) => {
        this.callbacks.setIsHarvesting(false);
        this.callbacks.setCurrentHarvestNodeId(null);
        this.callbacks.hideHarvestUI();

        this.scene.events.emit("harvest:complete", {
          nodeId: data.nodeId,
          drops: data.drops || [],
          xpGained: data.xpGained,
          bonusApplied: data.bonusApplied,
          skillType: data.skillType,
        });
      },
    );

    this.socket.on("resource:harvest_cancelled", (data: { nodeId: string }) => {
      if (this.callbacks.getCurrentHarvestNodeId() === data.nodeId) {
        this.callbacks.setIsHarvesting(false);
        this.callbacks.setCurrentHarvestNodeId(null);
        this.callbacks.hideHarvestUI();
        this.scene.events.emit("harvest:cancelled", { nodeId: data.nodeId });
      }
    });

    this.socket.on("resource:depleted", (data: { nodeId: string }) => {
      this.callbacks.removeResourceNode(data.nodeId);
    });

    this.socket.on("resource:error", (data: { message: string }) => {
      this.callbacks.showFloatingMessage(data.message, "#ff6666");
    });
  }

  private setupBroadcastHandlers(): void {
    if (!this.socket) return;

    this.socket.on(
      "global:broadcast",
      (data: { message: string; type: string; duration: number }) => {
        console.log(`📢 Global broadcast: ${data.message}`);
        this.callbacks.showGlobalBroadcast(
          data.message,
          data.type,
          data.duration,
        );
      },
    );

    this.socket.on(
      "chat:message",
      (data: {
        senderId: string;
        senderName: string;
        shipColor?: string | number;
        message: string;
        position: { x: number; y: number };
      }) => {
        this.callbacks.showChatMessage(
          data.senderId,
          data.senderName,
          data.shipColor,
          data.message,
          data.position,
        );
      },
    );
  }

  private setupTerritoryHandlers(): void {
    if (!this.socket) return;

    this.socket.on("territory:update", (data: any) => {
      console.log(
        `🏝️ Territory update: ${data.islandId} now owned by ${data.ownerName || "nobody"}`,
      );
      this.callbacks.updateIslandOwnership(data.islandId, data);
    });

    this.socket.on("territory:error", (data: { message: string }) => {
      this.callbacks.showFloatingMessage(data.message, "#ff6666");
    });
  }

  private setupDockingHandlers(): void {
    if (!this.socket) return;

    this.socket.on(
      "ship:dockSuccess",
      (data: {
        islandId: string;
        islandName?: string;
        hasShop?: boolean;
        hasShipyard?: boolean;
        isHarborOffice?: boolean;
        hasCourier?: boolean;
        hasDockServices?: boolean;
        message: string;
      }) => {
        console.log(`⚓ Docked at island: ${data.islandId}`);

        const localShip = this.callbacks.getLocalShip();
        if (
          localShip?.shipData.combatState === "docked" &&
          localShip.shipData.dockedAt === data.islandId
        ) {
          return;
        }

        this.callbacks.showFloatingMessage(data.message, "#2ecc71");
        playSound(SFX_KEYS.DOCK_ARRIVE);

        if (localShip) {
          localShip.shipData.dockedAt = data.islandId;
          localShip.shipData.combatState = "docked";
        }

        const islandRenderer = this.callbacks.getIslandRenderer();
        const permanentRenderer = this.callbacks.getPermanentLocationRenderer();
        const islandData =
          islandRenderer?.getIslandData(data.islandId) ||
          permanentRenderer?.getLocationData(data.islandId);

        const resolvedIslandName =
          islandData?.customName ||
          islandData?.permanentName ||
          data.islandName ||
          data.islandId;
        const islandIdLower = (data.islandId || "").toLowerCase();
        const islandNameLower = (resolvedIslandName || "").toLowerCase();

        const isTradingPost =
          data.hasShop ?? islandIdLower.includes("trading_post");
        const isShipyard =
          data.hasShipyard ?? islandIdLower.includes("shipyard");
        const isHarborOffice =
          data.isHarborOffice ?? islandIdLower.includes("harbor_office");
        const hasCourier = data.hasCourier ?? (isTradingPost || isHarborOffice);
        const hasTavern =
          islandIdLower.includes("tavern") ||
          islandNameLower.includes("tavern");
        const isPermanent =
          isTradingPost || isShipyard || isHarborOffice || hasTavern;
        const hasDockServices = data.hasDockServices ?? isPermanent;

        this.scene.events.emit("player:docked", {
          islandId: data.islandId,
          islandName: resolvedIslandName,
          hasShop: isTradingPost,
          hasShipyard: isShipyard,
          isHarborOffice,
          hasCourier,
          hasTavern,
          hasDockServices,
          isPermanent,
        });
      },
    );

    this.socket.on("ship:dockError", (data: { message: string }) => {
      this.callbacks.showFloatingMessage(data.message, "#ff6666");
    });

    this.socket.on(
      "ship:undockSuccess",
      (data: { position: { x: number; y: number }; message: string }) => {
        console.log(`⚓ Undocked`);
        this.callbacks.showFloatingMessage(data.message, "#3498db");
        const localShip = this.callbacks.getLocalShip();
        if (localShip) {
          localShip.shipData.dockedAt = undefined;
          localShip.shipData.combatState = "sailing";
        }
        this.scene.events.emit("player:undocked");
      },
    );

    this.socket.on("ship:undockError", (data: { message: string }) => {
      this.callbacks.showFloatingMessage(data.message, "#ff6666");
    });

    this.socket.on(
      "ship:dockServices:result",
      (data: { success: boolean; message: string; heat: number }) => {
        const color = data.success ? "#22c55e" : "#ff6666";
        this.callbacks.showFloatingMessage(data.message, color);
      },
    );

    this.socket.on("player:undocked", () => {
      console.log(`⚓ Auto-undocked by server`);
      const localShip = this.callbacks.getLocalShip();
      if (localShip) {
        localShip.shipData.dockedAt = undefined;
        localShip.shipData.combatState = "sailing";
      }
      this.scene.events.emit("player:undocked");
    });
  }

  private setupLootContainerHandlers(): void {
    if (!this.socket) return;

    this.socket.on("lootcontainer:list", (data: any) => {
      // Handle case where data might not be an array
      if (!Array.isArray(data)) {
        console.warn("🚨 lootcontainer:list received non-array data:", data);
        return;
      }

      for (const c of data) {
        this.callbacks.spawnLootContainerMarker(c.id, c.x, c.y, c.isPrivate);
      }
    });

    this.socket.on(
      "lootcontainer:spawned",
      (data: { id: string; x: number; y: number; isPrivate: boolean }) => {
        console.log(`📦 Loot container spawned: ${data.id}`);
        this.callbacks.spawnLootContainerMarker(
          data.id,
          data.x,
          data.y,
          data.isPrivate,
        );
      },
    );

    this.socket.on("lootcontainer:despawned", (data: { id: string }) => {
      this.callbacks.removeLootContainerMarker(data.id);
    });
  }

  private setupShopHandlers(): void {
    if (!this.socket) return;

    this.socket.on("shop:data", (data: { shop: ShopData }) => {
      console.log(`🏪 Received shop data: ${data.shop.name}`);
      this.callbacks.displayShopUI(data.shop);
      playSound(SFX_KEYS.SHOP_OPEN);
    });

    this.socket.on(
      "shop:purchase",
      (data: {
        success: boolean;
        itemId: string;
        quantity: number;
        cost: number;
        newBalance: number;
      }) => {
        console.log(
          `💰 Purchase: ${data.itemId} x${data.quantity} for ${data.cost} pieces`,
        );
        this.callbacks.showPurchaseFeedback(data.cost);
        playSound(SFX_KEYS.SHOP_PURCHASE);
        playSound(SFX_KEYS.COINS_SPEND);
      },
    );

    this.socket.on("shop:error", (data: { message: string }) => {
      console.log(`❌ Shop error: ${data.message}`);
      playSound(SFX_KEYS.UI_ERROR);
    });
  }

  private setupInventoryAndPlayerHandlers(): void {
    if (!this.socket) return;

    // Handle inventory updates from loot, harvesting, etc
    this.socket.on(
      "inventory:add",
      (data: { itemId: string; quantity: number }) => {
        console.log(`✅ Added to inventory: ${data.quantity}x ${data.itemId}`);
      },
    );

    // Handle player stat updates (currency, infamy, etc)
    this.socket.on(
      "player:update",
      (data: { pieces?: number; infamy?: number }) => {
        if (data.pieces !== undefined) {
          console.log(`💰 Pieces updated: ${data.pieces}`);
        }
        if (data.infamy !== undefined) {
          console.log(`⭐ Infamy updated: ${data.infamy}`);
        }
      },
    );
  }

  private setupRiftHandlers(): void {
    if (!this.socket) return;

    // Handle rift restoration on login (player was in a rift when they logged out)
    this.socket.on(
      "rift:restored",
      async (data: { riftId: string; position: { x: number; y: number } }) => {
        console.log(
          `🌀 [RIFT] Restoring player to rift: ${data.riftId} at (${data.position.x}, ${data.position.y})`,
        );

        // Set rift state
        this.callbacks.setIsInRiftInstance(true);
        this.callbacks.setCurrentRiftId(data.riftId);

        // Clear chunk cache to prevent core world data from persisting
        try {
          const CacheManager = require("../../utils/CacheManager").default;
          CacheManager.clearChunks();
          console.log("[RIFT RESTORE] Chunk cache cleared");
        } catch (e) {
          console.warn("[RIFT RESTORE] Could not clear chunk cache:", e);
        }

        // Hide overworld visuals
        const islandRenderer = this.callbacks.getIslandRenderer();
        if (islandRenderer) {
          islandRenderer.clearIslands();
        }

        const fishingRenderer = this.callbacks.getFishingSpotRenderer();
        if (fishingRenderer) {
          fishingRenderer.clearSpots();
        }

        // Clear permanent locations
        const permanentRenderer = this.callbacks.getPermanentLocationRenderer();
        if (permanentRenderer) {
          permanentRenderer.getLocations().forEach((entry: any) => {
            entry.graphics?.destroy();
            entry.hitArea?.destroy();
            entry.label?.destroy();
            entry.typeLabel?.destroy();
          });
          permanentRenderer.getLocations().clear();
        }

        // Clear ambient NPCs (they only exist in core world)
        const ambientNPCs = this.callbacks.getAmbientNPCs();
        if (ambientNPCs) {
          ambientNPCs.forEach((npc: any) => {
            npc.destroy();
          });
          ambientNPCs.clear();
        }

        // Load rift scars for this rift
        const riftScarManager = this.callbacks.getRiftScarManager();
        if (riftScarManager) {
          await riftScarManager.loadScarsForRift(data.riftId);
        }

        // Emit event for React UI (this triggers rift metadata fetch)
        this.scene.events.emit("rift:entered", {
          riftId: data.riftId,
          position: data.position,
        });

        this.callbacks.showFloatingMessage(
          `Restored to ${data.riftId}`,
          "#00ffff",
        );
      },
    );
  }

  private setupConstructionHandlers(): void {
    if (!this.socket) return;

    this.socket.on(
      "construction:placed",
      (data: {
        id: string;
        worldX: number;
        worldY: number;
        materialType: string;
        characterName?: string;
      }) => {
        console.log(
          `🔨 Construction placed: ${data.materialType} at (${data.worldX}, ${data.worldY})`,
        );
        this.callbacks.showFloatingMessage(
          `${data.characterName || "Player"} placed a ${data.materialType} tile`,
          "#66ff66",
        );
      },
    );

    this.socket.on("construction:error", (data: { message: string }) => {
      console.log(`🔨 Construction error: ${data.message}`);
      this.callbacks.showFloatingMessage(data.message, "#ff6666");
    });

    // Listen for construction updates from server (tile placement/removal)
    this.socket.on(
      "construction:update",
      (data: {
        action: "place" | "remove";
        chunkId: string;
        construction: {
          id: string;
          worldX: number;
          worldY: number;
          materialType: string;
          characterName?: string;
        };
      }) => {
        console.log(
          `🔨 [SocketHandler] Received construction:update - action: ${data.action}, tile at (${data.construction.worldX}, ${data.construction.worldY})`,
        );

        // Use incremental rendering with PlacedTileRenderer instead of full chunk reload
        const placedTileRenderer = this.callbacks.getPlacedTileRenderer?.();
        if (placedTileRenderer) {
          if (data.action === "place") {
            // Check if tile already exists at this position (from optimistic rendering)
            const hasTile = (placedTileRenderer as any).hasTileAt?.(
              data.construction.worldX,
              data.construction.worldY,
            );

            if (hasTile) {
              // Update the existing optimistic tile's ID instead of rendering a new one
              console.log(
                `🔨 [SocketHandler] Tile already exists at (${data.construction.worldX}, ${data.construction.worldY}), updating ID only`,
              );
              // Note: We don't know the temp ID here, so we'll just skip rendering
              // The optimistic tile will remain with its temp ID but that's okay
            } else {
              // Render single tile incrementally (for other players' tiles)
              if ((placedTileRenderer as any).renderSingleConstructionTile) {
                (placedTileRenderer as any).renderSingleConstructionTile(
                  data.construction.worldX,
                  data.construction.worldY,
                  data.construction.materialType,
                  data.construction.id,
                );
                console.log(
                  `🔨 [SocketHandler] Incrementally rendered ${data.construction.materialType} tile at (${data.construction.worldX}, ${data.construction.worldY})`,
                );
              }
            }
          } else if (data.action === "remove") {
            // Remove single tile incrementally
            if ((placedTileRenderer as any).removeSingleConstructionTile) {
              (placedTileRenderer as any).removeSingleConstructionTile(
                data.construction.worldX,
                data.construction.worldY,
                data.construction.id,
              );
              console.log(
                `🔨 [SocketHandler] Incrementally removed tile at (${data.construction.worldX}, ${data.construction.worldY})`,
              );
            }
          }
        }

        // Still invalidate cache for next chunk reload
        CacheInvalidation.invalidateChunk(data.chunkId);
        console.log(
          `🔨 [SocketHandler] Invalidated chunk ${data.chunkId} cache`,
        );
      },
    );

    // Listen for initial construction load when chunks are registered
    this.socket.on(
      "construction:initial_load",
      (data: {
        constructions: Array<{
          id: string;
          worldX: number;
          worldY: number;
          materialType: string;
          characterName?: string;
        }>;
      }) => {
        console.log(
          `🔨 [SocketHandler] Received construction:initial_load with ${data.constructions.length} tiles`,
        );

        const placedTileRenderer = this.callbacks.getPlacedTileRenderer?.();
        if (
          placedTileRenderer &&
          (placedTileRenderer as any).renderSingleConstructionTile
        ) {
          // Render each construction tile
          for (const construction of data.constructions) {
            (placedTileRenderer as any).renderSingleConstructionTile(
              construction.worldX,
              construction.worldY,
              construction.materialType,
              construction.id,
            );
            console.log(
              `🔨 [SocketHandler] Loaded ${construction.materialType} tile at (${construction.worldX}, ${construction.worldY})`,
            );
          }
        }
      },
    );
  }

  // Getters for fishing state
  getCurrentFishingSpotType(): string {
    return this.currentFishingSpotType;
  }

  getCurrentFishingSpotId(): string {
    return this.currentFishingSpotId;
  }
}
